import { calculateSimilarity } from './levenshtein';
import { SpeechSession } from './SpeechSession';
import type { SessionEndReason } from './SpeechSession';

export interface SttSegment {
  text: string;
  sttCreatedStartAt: number;
  sttCreatedEndAt: number;
  sttConfidence: number | null;
}

export interface SpeechRecognizerConfig {
  lang: string;
  continuous: boolean;
  maxConsecutiveErrors?: number;
}

export interface SpeechRecognizerCallbacks {
  onSegmentComplete: (segment: SttSegment) => void;

  /**
   * Manager / Session 내부에서 사용하는 음수 에러 코드.
   * 양수 코드는 SpeechSession의 ERROR_CODE_MAP(Web Speech API 에러)에 정의되어 있다.
   *
   * -1: SpeechRecognition API 미지원 또는 알 수 없는 에러
   * -2: SpeechRecognition.start() 호출 실패 (이미 활성화된 세션 등)
   * -3: rapid restart 횟수 초과 (세션이 시작 직후 반복 종료)
   */
  onError: (error: { code: number; message: string }) => void;
  onStateChange?: (active: boolean) => void;
}

/** 연속 에러 최대 허용 횟수. 초과 시 STT 중단. @default 5 */
const DEFAULT_MAX_CONSECUTIVE_ERRORS = 5;
/** iOS(errorRetryDelay=500ms), Android(RETRY_LISTENING_DEBOUNCE_TIME=200ms)와 동일 수준 */
const ERROR_RETRY_DELAY_MS = 500;
const RESTART_DELAY_MS = 300;

/**
 * 세션 재시작 시 중복 segment 방지를 위한 Levenshtein 유사도 임계값 (0~1).
 * - iOS: MatchVideoChatSTTProvider.isSimilarToLastSentText (threshold 0.8)
 * - Android: ManualSegmentActor (Levenshtein 기반 유사도 비교)
 */
const SIMILARITY_THRESHOLD = 0.8;

/**
 * Web Speech API 세션이 시작 직후 onend로 종료되는 경우가 있다:
 *  - getUserMedia와 마이크 접근이 충돌할 때 (영상통화 중 STT 시작)
 *  - iOS Safari에서 백그라운드 전환 후 복귀 시
 *  - 브라우저가 권한을 즉시 거부하지만 not-allowed 에러 대신 onend만 발생하는 경우
 *
 * 이를 무한 재시작 루프 없이 처리하기 위해:
 * 1) rapid end 연속 → rapidRestartCount 증가
 * 2) MAX_RAPID_RESTARTS 초과 → 근본 문제로 판단하고 STT 중단
 * 3) RAPID_RESTART_BACKOFF_MS 대기 후 재시도 → 일시적 충돌 해소 시간 확보
 */
const MAX_RAPID_RESTARTS = 3;
const RAPID_RESTART_BACKOFF_MS = 2000;

function getSpeechRecognitionConstructor(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  return (
    (typeof SpeechRecognition !== 'undefined' && SpeechRecognition) ||
    (typeof webkitSpeechRecognition !== 'undefined' && webkitSpeechRecognition) ||
    null
  );
}

export function isSpeechRecognitionSupported(): boolean {
  return getSpeechRecognitionConstructor() !== null;
}

/**
 * Manager는 세션 간 전환 정책(retry, backoff, 포기)만 담당한다.
 * 세션 내부 로직(onresult, onerror, onend, silence detection)은 SpeechSession이 처리한다.
 */
export class SpeechRecognizerManager {
  private config: SpeechRecognizerConfig;

  private callbacks: SpeechRecognizerCallbacks;

  private isActive = false;

  private currentSession: SpeechSession | null = null;

  private restartTimer: ReturnType<typeof setTimeout> | null = null;

  private consecutiveErrorCount = 0;

  private rapidRestartCount = 0;

  private lastSentText = '';

  constructor(config: SpeechRecognizerConfig, callbacks: SpeechRecognizerCallbacks) {
    this.config = config;
    this.callbacks = callbacks;
  }

  start(): boolean {
    const Ctor = getSpeechRecognitionConstructor();
    if (!Ctor) {
      this.callbacks.onError({ code: -1, message: 'SpeechRecognition not supported' });
      return false;
    }

    this.isActive = true;
    this.clearRestartTimer();
    this.consecutiveErrorCount = 0;
    this.rapidRestartCount = 0;
    this.createSession(Ctor);
    this.callbacks.onStateChange?.(true);
    return true;
  }

  stop(): void {
    this.isActive = false;
    this.clearRestartTimer();
    this.disposeCurrentSession();
    this.lastSentText = '';
    this.callbacks.onStateChange?.(false);
  }

  updateConfig(config: Partial<SpeechRecognizerConfig>): void {
    const needsRestart = this.isActive;
    if (needsRestart) this.stop();

    this.config = { ...this.config, ...config };

    if (needsRestart) this.start();
  }

  getIsActive(): boolean {
    return this.isActive;
  }

  private createSession(Ctor?: new () => SpeechRecognition): void {
    if (!this.isActive) return;

    this.disposeCurrentSession();

    const Constructor = Ctor ?? getSpeechRecognitionConstructor();
    if (!Constructor) return;

    this.currentSession = new SpeechSession(Constructor, this.config, {
      onPartialResult: () => {
        if (!this.isActive) return;
        this.consecutiveErrorCount = 0;
        this.rapidRestartCount = 0;
      },
      onSegmentComplete: (segment) => {
        if (!this.isActive) return;
        this.emitSegment(segment);
      },
      onError: (error) => {
        if (!this.isActive) return;
        this.callbacks.onError(error);
      },
      onSessionEnd: (reason) => {
        if (!this.isActive) return;
        this.handleSessionEnd(reason);
      },
    });

    this.currentSession.start();
  }

  private handleSessionEnd(reason: SessionEndReason): void {
    this.disposeCurrentSession();

    switch (reason) {
      case 'fatal': {
        this.stop();
        break;
      }
      case 'rapid': {
        this.rapidRestartCount++;
        if (this.rapidRestartCount >= MAX_RAPID_RESTARTS) {
          this.callbacks.onError({
            code: -3,
            message: `Session keeps ending immediately (${this.rapidRestartCount} times). Possible cause: mic conflict with getUserMedia or iOS Safari limitation.`,
          });
          this.stop();
          return;
        }
        this.scheduleRestart(RAPID_RESTART_BACKOFF_MS);
        break;
      }
      case 'error': {
        this.consecutiveErrorCount++;
        const maxErrors = this.config.maxConsecutiveErrors ?? DEFAULT_MAX_CONSECUTIVE_ERRORS;
        if (this.consecutiveErrorCount >= maxErrors) {
          this.stop();
          return;
        }
        this.scheduleRestart(ERROR_RETRY_DELAY_MS);
        break;
      }
      case 'natural':
      case 'silence': {
        this.consecutiveErrorCount = 0;
        this.rapidRestartCount = 0;
        this.scheduleRestart(RESTART_DELAY_MS);
        break;
      }
      default:
        break;
    }
  }

  private scheduleRestart(delayMs: number): void {
    this.clearRestartTimer();
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.isActive) {
        this.createSession();
      }
    }, delayMs);
  }

  private disposeCurrentSession(): void {
    if (!this.currentSession) return;

    const pendingSegment = this.currentSession.dispose();
    if (pendingSegment) {
      this.emitSegment(pendingSegment);
    }
    this.currentSession = null;
  }

  private emitSegment(segment: SttSegment): void {
    if (!segment.text.trim()) return;
    if (this.isSimilarToLastSent(segment.text)) return;

    this.lastSentText = segment.text;
    this.callbacks.onSegmentComplete(segment);
  }

  private isSimilarToLastSent(newText: string): boolean {
    if (!this.lastSentText) return false;
    if (newText === this.lastSentText) return true;

    const minLen = Math.min(newText.length, this.lastSentText.length);
    const maxLen = Math.max(newText.length, this.lastSentText.length);
    if (maxLen === 0) return true;
    if (minLen / maxLen < SIMILARITY_THRESHOLD) return false;

    return calculateSimilarity(newText, this.lastSentText) >= SIMILARITY_THRESHOLD;
  }

  private clearRestartTimer(): void {
    if (this.restartTimer) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
  }
}
